import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { setToastContextGetter } from '@/hooks/ui/use-toast';

interface HighlightedItem {
  type: 'shift' | 'employee' | 'review';
  id: string;
}

interface DashboardTabContextProps {
  businessDashboardTab: string;
  employeeDashboardTab: string;
  employeePortalTab: string;
  franchiseDashboardTab: string;
  selectedShiftDate: Date | null;
  selectedBusinessName: string | null;
  isAccountSettingsOpen: boolean;
  highlightedItem: HighlightedItem | null;
  navigateToShiftMonth: Date | null;
  shiftToSelect: string | null; // Shift ID to select for editing when navigating
  setBusinessDashboardTab: (tab: string) => void;
  setEmployeeDashboardTab: (tab: string) => void;
  setEmployeePortalTab: (tab: string) => void;
  setFranchiseDashboardTab: (tab: string) => void;
  setSelectedShiftDate: (date: Date | null) => void;
  setSelectedBusinessName: (name: string | null) => void;
  setIsAccountSettingsOpen: (isOpen: boolean) => void;
  setHighlightedItem: (item: HighlightedItem | null) => void;
  setNavigateToShiftMonth: (date: Date | null) => void;
  setShiftToSelect: (shiftId: string | null) => void;
}

const DashboardTabContext = createContext<DashboardTabContextProps | undefined>(undefined);

interface DashboardTabProviderProps {
  children: ReactNode;
}

export function DashboardTabProvider({ children }: DashboardTabProviderProps) {
  const [businessDashboardTab, setBusinessDashboardTabState] = useState<string>(() => {
    try {
      const storedTab = localStorage.getItem('business-dashboard-tab');
      return storedTab || 'insights';
    } catch (e) {
      return 'insights';
    }
  });

  const [employeeDashboardTab, setEmployeeDashboardTabState] = useState<string>(() => {
    try {
      const storedTab = localStorage.getItem('employee-dashboard-tab');
      return storedTab || 'insights';
    } catch (e) {
      return 'insights';
    }
  });

  const [employeePortalTab, setEmployeePortalTabState] = useState<string>(() => {
    try {
      const storedTab = localStorage.getItem('employee-portal-tab');
      return storedTab || 'my-dashboard';
    } catch (e) {
      return 'my-dashboard';
    }
  });

  const [franchiseDashboardTab, setFranchiseDashboardTabState] = useState<string>(() => {
    try {
      const storedTab = localStorage.getItem('franchise-dashboard-tab');
      return storedTab || 'overview';
    } catch (e) {
      return 'overview';
    }
  });

  const [selectedShiftDate, setSelectedShiftDateState] = useState<Date | null>(() => {
    try {
      const storedDate = localStorage.getItem('selected-shift-date');
      return storedDate ? new Date(storedDate) : null;
    } catch (e) {
      return null;
    }
  });

  const [selectedBusinessName, setSelectedBusinessNameState] = useState<string | null>(null);
  const [isAccountSettingsOpen, setIsAccountSettingsOpenState] = useState<boolean>(false);
  const [highlightedItem, setHighlightedItemState] = useState<HighlightedItem | null>(null);
  const [navigateToShiftMonth, setNavigateToShiftMonthState] = useState<Date | null>(null);
  const [shiftToSelect, setShiftToSelectState] = useState<string | null>(null);

  // Auto-clear highlight after 3 seconds
  useEffect(() => {
    if (highlightedItem) {
      const timer = setTimeout(() => setHighlightedItemState(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedItem]);

  const setBusinessDashboardTab = (tab: string) => {
    try {
      localStorage.setItem('business-dashboard-tab', tab);
    } catch (e) {
      // Ignore localStorage errors
    }
    setBusinessDashboardTabState(tab);
  };

  const setEmployeeDashboardTab = (tab: string) => {
    try {
      localStorage.setItem('employee-dashboard-tab', tab);
    } catch (e) {
      // Ignore localStorage errors
    }
    setEmployeeDashboardTabState(tab);
  };

  const setEmployeePortalTab = (tab: string) => {
    try {
      localStorage.setItem('employee-portal-tab', tab);
    } catch (e) {
      // Ignore localStorage errors
    }
    setEmployeePortalTabState(tab);
  };

  const setFranchiseDashboardTab = (tab: string) => {
    try {
      localStorage.setItem('franchise-dashboard-tab', tab);
    } catch (e) {
      // Ignore localStorage errors
    }
    setFranchiseDashboardTabState(tab);
  };

  const setSelectedShiftDate = (date: Date | null) => {
    try {
      if (date) {
        localStorage.setItem('selected-shift-date', date.toISOString());
      } else {
        localStorage.removeItem('selected-shift-date');
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    setSelectedShiftDateState(date);
  };

  const setSelectedBusinessName = (name: string | null) => {
    setSelectedBusinessNameState(name);
  };

  const setIsAccountSettingsOpen = (isOpen: boolean) => {
    setIsAccountSettingsOpenState(isOpen);
  };

  const setHighlightedItem = (item: HighlightedItem | null) => {
    setHighlightedItemState(item);
  };

  const setNavigateToShiftMonth = (date: Date | null) => {
    setNavigateToShiftMonthState(date);
  };

  const setShiftToSelect = (shiftId: string | null) => {
    setShiftToSelectState(shiftId);
  };

  // Set up toast context getter
  useEffect(() => {
    const getTabCategory = (tab: string): string => {
      const tabMap: Record<string, string> = {
        'insights': 'Dashboard',
        'reviews': 'Reviews',
        'loyalty': 'Loyalty Program',
        'wallet': 'Digital Wallet',
        'coupons': 'Coupons',
        'qr-codes': 'QR Codes',
        'bulk-mail': 'Bulk Mail',
        'employees': 'Employee Management',
        'shifts': 'Shift Management',
        'payroll': 'Payroll',
        'recruitment': 'Recruitment',
        'analytics': 'Analytics',
        'communications': 'Communications',
        'business-settings': 'Business Settings',
        'my-dashboard': 'My Dashboard',
        'my-shifts': 'My Shifts',
        'my-time': 'Time Tracking',
        'my-payroll': 'My Payroll',
      };
      return tabMap[tab] || tab;
    };

    setToastContextGetter(() => {
      // Get the current path from window.location instead of useLocation hook
      const path = window.location.pathname;
      let category = 'System';

      // Check if account settings is open first
      if (isAccountSettingsOpen) {
        category = 'Account Settings';
      } else if (path.includes('/dashboard')) {
        category = getTabCategory(businessDashboardTab);
      } else if (path.includes('/employee-dashboard')) {
        category = getTabCategory(employeeDashboardTab);
      } else if (path.includes('/employee-portal')) {
        category = getTabCategory(employeePortalTab);
      } else if (path.includes('/settings')) {
        category = 'Settings';
      } else if (path.includes('/business')) {
        category = 'Business Management';
      } else if (path.includes('/auth') || path.includes('/sign')) {
        category = 'Authentication';
      }

      return {
        category,
        businessName: selectedBusinessName || undefined,
      };
    });
  }, [businessDashboardTab, employeeDashboardTab, employeePortalTab, selectedBusinessName, isAccountSettingsOpen]);

  return (
    <DashboardTabContext.Provider value={{
      businessDashboardTab,
      employeeDashboardTab,
      employeePortalTab,
      franchiseDashboardTab,
      selectedShiftDate,
      selectedBusinessName,
      isAccountSettingsOpen,
      highlightedItem,
      navigateToShiftMonth,
      shiftToSelect,
      setBusinessDashboardTab,
      setEmployeeDashboardTab,
      setEmployeePortalTab,
      setFranchiseDashboardTab,
      setSelectedShiftDate,
      setSelectedBusinessName,
      setIsAccountSettingsOpen,
      setHighlightedItem,
      setNavigateToShiftMonth,
      setShiftToSelect
    }}>
      {children}
    </DashboardTabContext.Provider>
  );
}

export const useDashboardTab = (): DashboardTabContextProps => {
  const context = useContext(DashboardTabContext);
  if (context === undefined) {
    throw new Error('useDashboardTab must be used within a DashboardTabProvider');
  }
  return context;
};

export { DashboardTabContext };